import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { useEffect } from 'react';

import {
  useEstablecimientos,
  useEstablecimientosByProfesional,
} from '@/features/establecimientos/hooks/useEstablecimientos';

interface EstablecimientoSelectProps {
  value: string;
  onChange: (uid: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  includeEmpty?: boolean;
  emptyLabel?: string;
  /**
   * Cuando se pasa, el selector filtra por ese profesional.
   * - string vacío → deshabilitado con "Seleccioná primero un profesional."
   * - string con uid → muestra solo los establecimientos de ese profesional
   * - undefined → muestra todos (comportamiento por defecto, sin filtro)
   */
  uidProfesional?: string;
}

export function EstablecimientoSelect({
  value,
  onChange,
  label = 'Establecimiento',
  required = false,
  disabled = false,
  error = false,
  fullWidth = true,
  size = 'medium',
  includeEmpty = false,
  emptyLabel = 'Todos',
  uidProfesional,
}: EstablecimientoSelectProps): JSX.Element {
  const filterMode = uidProfesional !== undefined;

  const allQuery = useEstablecimientos({ limit: 100 }, !filterMode);
  const byProfQuery = useEstablecimientosByProfesional(uidProfesional || undefined, { limit: 100 });

  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = filterMode
    ? byProfQuery
    : allQuery;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isProfesionalPending = filterMode && !uidProfesional;
  const isLoadingAll = isLoading || hasNextPage;
  const isEmpty = filterMode && !!uidProfesional && !isLoading && !hasNextPage && items.length === 0;

  const selectDisabled = disabled || isLoadingAll || isProfesionalPending;
  const resolvedLabel = isProfesionalPending
    ? 'Seleccioná primero un profesional.'
    : isLoadingAll
      ? 'Cargando...'
      : label;

  function handleChange(e: SelectChangeEvent): void {
    onChange(e.target.value);
  }

  return (
    <Box sx={fullWidth ? { width: '100%' } : undefined}>
      <FormControl
        fullWidth={fullWidth}
        required={required}
        error={error}
        disabled={selectDisabled}
        size={size}
      >
        <InputLabel>{resolvedLabel}</InputLabel>
        <Select
          value={isProfesionalPending ? '' : value}
          label={resolvedLabel}
          onChange={handleChange}
          endAdornment={
            isLoadingAll ? <CircularProgress size={16} sx={{ mr: 3 }} /> : undefined
          }
        >
          {includeEmpty && (
            <MenuItem value="">
              <em>{emptyLabel}</em>
            </MenuItem>
          )}
          {items.map((item) => (
            <MenuItem key={item.uid_establecimiento} value={item.uid_establecimiento}>
              {item.firma_ganaderos ? `${item.nombre} — ${item.firma_ganaderos}` : item.nombre}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isEmpty && (
        <FormHelperText sx={{ mx: '14px' }}>
          Este profesional no tiene establecimientos asociados.
        </FormHelperText>
      )}
    </Box>
  );
}
